import { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import {
  Phone, MessageCircle, MapPin, Loader2, CheckCircle2,
  AlertCircle, Send, Crosshair,
} from "lucide-react";
import { SERVICES } from "../data/services";
import { PHONE_TEL, PHONE_DISPLAY, waLink } from "../data/site";

export default function ContactForm({ defaultService = "" }) {
  const [name, setName] = useState("");
  const [phone, setPhone] = useState("");
  const [service, setService] = useState(defaultService);
  const [location, setLocation] = useState("");
  const [message, setMessage] = useState("");
  const [geo, setGeo] = useState(null);
  const [geoStatus, setGeoStatus] = useState("idle"); // idle | locating | detected | denied | error

  const detectLocation = () => {
    if (!navigator.geolocation) {
      setGeoStatus("error");
      return;
    }
    setGeoStatus("locating");
    navigator.geolocation.getCurrentPosition(
      async (pos) => {
        const { latitude, longitude, accuracy } = pos.coords;
        setGeo({ latitude, longitude, accuracy });
        try {
          const r = await fetch(
            `https://api.bigdatacloud.net/data/reverse-geocode-client?latitude=${latitude}&longitude=${longitude}&localityLanguage=en`
          );
          const d = await r.json();
          const addr = [d.locality, d.city, d.principalSubdivision, d.postcode]
            .filter(Boolean)
            .join(", ");
          const resolved = addr || `${latitude.toFixed(5)}, ${longitude.toFixed(5)}`;
          setGeo({ latitude, longitude, accuracy, address: resolved });
          setLocation((prev) => prev || resolved);
        } catch {
          setGeo({ latitude, longitude, accuracy });
        }
        setGeoStatus("detected");
      },
      (err) => setGeoStatus(err.code === 1 ? "denied" : "error"),
      { enableHighAccuracy: true, timeout: 12000, maximumAge: 30000 }
    );
  };

  const submit = (e) => {
    e.preventDefault();
    const lines = [
      "NEW ENQUIRY — MOTION TYRES",
      `Name: ${name}`,
      `Phone: ${phone}`,
      `Service: ${service || "Not sure yet"}`,
      `Location: ${location || "Not provided"}`,
    ];
    if (geo) {
      lines.push(
        `GPS pin: ${geo.latitude.toFixed(5)}, ${geo.longitude.toFixed(5)} (±${Math.round(geo.accuracy)}m)`
      );
      lines.push(`Map: https://maps.google.com/?q=${geo.latitude},${geo.longitude}`);
    }
    if (message) lines.push(`Details: ${message}`);
    window.open(waLink(lines.join("\n")), "_blank");
  };

  const statusContent = {
    locating: {
      icon: <Loader2 className="w-4 h-4 animate-spin text-[#FF5500]" />,
      text: "Finding you… allow location access if prompted.",
      cls: "text-zinc-400",
    },
    detected: {
      icon: <CheckCircle2 className="w-4 h-4 text-emerald-400" />,
      text: geo?.address
        ? `Location found: ${geo.address}`
        : `Location found: ${geo?.latitude?.toFixed(5)}, ${geo?.longitude?.toFixed(5)}`,
      cls: "text-emerald-400",
    },
    denied: {
      icon: <AlertCircle className="w-4 h-4 text-[#FF8800]" />,
      text: "Location blocked — type your address instead and we'll find you by phone.",
      cls: "text-[#FF8800]",
    },
    error: {
      icon: <AlertCircle className="w-4 h-4 text-red-400" />,
      text: "Couldn't detect location — please type your address below.",
      cls: "text-red-400",
    },
  };
  const status = statusContent[geoStatus];

  return (
    <form
      onSubmit={submit}
      data-testid="contact-form"
      className="bg-[#131316] border border-[#27272A] p-6 sm:p-8 rounded-sm"
    >
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        <div>
          <label htmlFor="cf-name" className="font-telemetry text-[10px] uppercase tracking-[0.25em] text-zinc-400 block mb-2">
            Your name *
          </label>
          <input
            id="cf-name"
            data-testid="contact-name-input"
            required
            value={name}
            onChange={(e) => setName(e.target.value)}
            placeholder="Alex Driver"
            className="w-full bg-[#0B0B0C] border border-[#27272A] focus:border-[#FF5500] focus:outline-none px-4 py-3 rounded-sm text-white placeholder-zinc-600"
          />
        </div>
        <div>
          <label htmlFor="cf-phone" className="font-telemetry text-[10px] uppercase tracking-[0.25em] text-zinc-400 block mb-2">
            Phone number *
          </label>
          <input
            id="cf-phone"
            data-testid="contact-phone-input"
            required
            type="tel"
            value={phone}
            onChange={(e) => setPhone(e.target.value)}
            placeholder="07XXX XXXXXX"
            className="w-full bg-[#0B0B0C] border border-[#27272A] focus:border-[#FF5500] focus:outline-none px-4 py-3 rounded-sm text-white placeholder-zinc-600"
          />
        </div>
        <div>
          <label htmlFor="cf-service" className="font-telemetry text-[10px] uppercase tracking-[0.25em] text-zinc-400 block mb-2">
            Service needed
          </label>
          <select
            id="cf-service"
            data-testid="contact-service-select"
            value={service}
            onChange={(e) => setService(e.target.value)}
            className="w-full bg-[#0B0B0C] border border-[#27272A] focus:border-[#FF5500] focus:outline-none px-4 py-3 rounded-sm text-white"
          >
            <option value="">Choose a service…</option>
            {SERVICES.map((s) => (
              <option key={s.slug} value={s.shortName}>{s.shortName}</option>
            ))}
            <option value="Not sure">Not sure — advise me</option>
          </select>
        </div>
        <div>
          <label htmlFor="cf-location" className="font-telemetry text-[10px] uppercase tracking-[0.25em] text-zinc-400 block mb-2">
            Your location
          </label>
          <input
            id="cf-location"
            data-testid="contact-location-input"
            value={location}
            onChange={(e) => setLocation(e.target.value)}
            placeholder="Postcode or street"
            className="w-full bg-[#0B0B0C] border border-[#27272A] focus:border-[#FF5500] focus:outline-none px-4 py-3 rounded-sm text-white placeholder-zinc-600"
          />
        </div>
      </div>

      <div className="mt-4">
        <button
          type="button"
          onClick={detectLocation}
          data-testid="detect-location-button"
          className="btn-glow flex items-center gap-2 px-5 py-3 rounded-full border border-[#FF5500]/60 text-[#FF5500] hover:bg-[#FF5500] hover:text-black font-telemetry text-[11px] font-semibold uppercase tracking-[0.15em] transition-colors"
        >
          <Crosshair className="w-4 h-4" />
          {geoStatus === "detected" ? "Location locked" : "Use my live location"}
        </button>
        <AnimatePresence>
          {status && (
            <motion.p
              initial={{ opacity: 0, y: -4 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0 }}
              data-testid="location-status"
              className={`flex items-center gap-2 mt-3 text-xs ${status.cls}`}
            >
              {status.icon} {status.text}
            </motion.p>
          )}
        </AnimatePresence>
      </div>

      <div className="mt-4">
        <label htmlFor="cf-message" className="font-telemetry text-[10px] uppercase tracking-[0.25em] text-zinc-400 block mb-2">
          Anything else? (car reg, tyre size, what happened…)
        </label>
        <textarea
          id="cf-message"
          data-testid="contact-message-input"
          rows={3}
          value={message}
          onChange={(e) => setMessage(e.target.value)}
          placeholder="e.g. Focus reg AB12 CDE, front nearside flat, parked on driveway"
          className="w-full bg-[#0B0B0C] border border-[#27272A] focus:border-[#FF5500] focus:outline-none px-4 py-3 rounded-sm text-white placeholder-zinc-600 resize-none"
        />
      </div>

      <div className="mt-6 flex flex-col sm:flex-row gap-3">
        <button
          type="submit"
          data-testid="contact-submit-button"
          className="btn-glow flex-1 flex items-center justify-center gap-2 py-4 rounded-full bg-[#16A34A] hover:bg-[#1AB055] text-black font-telemetry text-sm font-semibold uppercase tracking-[0.12em] transition-colors"
        >
          <Send className="w-4 h-4" /> Send via WhatsApp
        </button>
        <a
          href={PHONE_TEL}
          data-testid="contact-call-button"
          className="flex-1 flex items-center justify-center gap-2 py-4 rounded-full bg-[#FF5500] hover:bg-[#FF6A1A] text-black font-telemetry text-sm font-semibold uppercase tracking-[0.12em] transition-colors"
        >
          <Phone className="w-4 h-4" /> Or call {PHONE_DISPLAY}
        </a>
      </div>
      <p className="mt-4 flex items-center gap-2 text-xs text-zinc-500">
        <MessageCircle className="w-3.5 h-3.5 text-emerald-500" />
        Your enquiry opens WhatsApp with everything pre-filled — just hit send.
        <MapPin className="w-3.5 h-3.5 text-[#FF5500] ml-1" />
        We cover 12 UK regions, 24/7.
      </p>
    </form>
  );
}
